import {
  type ConversionsByTrial,
  type ConversionsByTrialDetail,
  type ConversionsByTrialDetailRequest,
  type ConversionsByTrialRequest
} from "@common/components/Report/Provider/Static/ConversionsByTrialProvider";
import {
  type TotalSalesByAccessCard,
  type TotalSalesByAccessCardRequest
} from "@common/components/Report/Provider/Static/TotalSalesByAccessCardProvider";
import {
  type TotalSalesByContact,
  type TotalSalesByContactRequest
} from "@common/components/Report/Provider/Static/TotalSalesByContactProvider";
import {
  type TotalSalesByCountry,
  type TotalSalesByCountryRequest
} from "@common/components/Report/Provider/Static/TotalSalesByCountryProvider";
import {
  type TotalSalesByDate,
  type TotalSalesByDateRequest
} from "@common/components/Report/Provider/Static/TotalSalesByDateProvider";
import {
  type TotalSalesByEvent,
  type TotalSalesByEventRequest
} from "@common/components/Report/Provider/Static/TotalSalesByEventProvider";
import {
  type TotalSalesByFunnel,
  type TotalSalesByFunnelRequest
} from "@common/components/Report/Provider/Static/TotalSalesByFunnelProvider";
import {
  type TotalSalesByList,
  type TotalSalesByListRequest
} from "@common/components/Report/Provider/Static/TotalSalesByListProvider";
import {
  type TotalSalesByTag,
  type TotalSalesByTagRequest
} from "@common/components/Report/Provider/Static/TotalSalesByTagProvider";
import { type TrafficByUrl, type TrafficByUrlRequest } from "@common/components/Report/Provider/Static/TrafficByUrlProvider";
import { type GenericMap } from "@common/declarations";
import apiV1 from "@common/lib/api";
import { type I18n } from "@common/lib/i18n";
import { type Api } from "@common/modules/system/types";
import { Common } from "@common/types";
import { type Data } from "./types";

export const getApi = (i18n: I18n) => ({
  get: (id: string): Promise<Data.Api.Report> => apiV1.get(i18n, `/data/reports/${id}`),
  getChartData: (id: string, parameters: GenericMap<any>): Promise<object[]> =>
    apiV1.put(i18n, `/data/reports/${id}/chart/data`, parameters),
  getFilters: (id: string, values: Api.GetFormFieldValue[]): Promise<Data.Api.Filter[]> =>
    apiV1.put(i18n, `/data/reports/${id}/filters`, { values }),
  getGridData: (id: string, parameters: GenericMap<any>): Promise<object[]> =>
    apiV1.put(i18n, `/data/reports/${id}/grid/data`, parameters),
  getFormFields: (id: string): Promise<Data.Api.FormField[]> => apiV1.get(i18n, `/data/reports/${id}/fields`),

  getGenealogyAggregates: (cycleEndDate: string, userId: number): Promise<Data.Api.GenealogyCycleAggregate[]> =>
    apiV1.get(i18n, `/data/reports/genealogyCycleAggregates`, { cycleEndDate, userId }, "memory"),

  getTrafficByUrl: (data: TrafficByUrlRequest): Promise<TrafficByUrl[]> =>
    apiV1.put(i18n, `/report/trafficByUrl`, data),

  getCategories: (): Promise<Data.Api.ReportCategory[]> => apiV1.get(i18n, `/data/reports/categories`),

  getConversionsByTrial: (data: ConversionsByTrialRequest): Promise<ConversionsByTrial[]> =>
    apiV1.put(i18n, `/report/conversionsByTrial`, data),

  getConversionsByTrialDetail: (data: ConversionsByTrialDetailRequest): Promise<ConversionsByTrialDetail[]> =>
    apiV1.put(i18n, `/report/conversionsByTrialDetail`, data),

  getTotalSalesByAccessCard: (data: TotalSalesByAccessCardRequest): Promise<TotalSalesByAccessCard[]> =>
    apiV1.put(i18n, `/report/totalSalesByAccessCard`, data),

  getTotalSalesByContact: (data: TotalSalesByContactRequest): Promise<TotalSalesByContact[]> =>
    apiV1.put(i18n, `/report/totalSalesByContact`, data),

  getTotalSalesByFunnel: (data: TotalSalesByFunnelRequest): Promise<TotalSalesByFunnel[]> =>
    apiV1.put(i18n, `/report/totalSalesByFunnel`, data),

  getTotalSalesByList: (data: TotalSalesByListRequest): Promise<TotalSalesByList[]> =>
    apiV1.put(i18n, `/report/totalSalesByList`, data),

  getTotalSalesByTag: (data: TotalSalesByTagRequest): Promise<TotalSalesByTag[]> =>
    apiV1.put(i18n, `/report/totalSalesByTag`, data),

  getTotalSalesByCountry: (data: TotalSalesByCountryRequest): Promise<TotalSalesByCountry[]> =>
    apiV1.put(i18n, `/report/totalSalesByCountry`, data),

  getTotalSalesByEvent: (data: TotalSalesByEventRequest): Promise<TotalSalesByEvent[]> =>
    apiV1.put(i18n, `/report/totalSalesByEvent`, data),

  getTotalSalesByDate: (data: TotalSalesByDateRequest): Promise<TotalSalesByDate[]> =>
    apiV1.put(i18n, `/report/totalSalesByDate`, data),

  getCommissionByUser: (data: Common.Api.PageRequest): Promise<Data.Api.CommissionByUser[]> =>
    apiV1.put(i18n, `/report/commissionByUser`, data)
});
